import { Text } from "@clipboard-health/ui-react";
import { Box, CircularProgress } from "@mui/material";
import { signInWithRedirect } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { type ReactElement, useEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk in SSO callback handler
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";

import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { fetchAuthSessionWithLock } from "../cognito/cognitoSessionLock";
import { initCognitoSession } from "../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../cognito/isSafeRedirectUrl";
import { RIPPLING_SSO_REQUESTED_URL_KEY } from "./startSsoRedirect";

const DEFAULT_REDIRECT_PATH = "/";
const SSO_CALLBACK_TIMEOUT_MS = 30_000;

function Logo() {
  return (
    <img
      alt="Clipboard"
      height={70}
      src={`${import.meta.env.BASE_URL}assets/clipboard-wordmark-brown.png`}
      style={{ width: "auto" }}
    />
  );
}

/**
 * Checks if the callback URL contains an SSO_ACCOUNT_LINKED error from the
 * PreSignUp Lambda. This happens on an employee's first SSO login when they
 * already have a Cognito account (via magic link/password). The Lambda links
 * the Rippling identity to the existing user and throws to prevent Cognito
 * from creating a duplicate — this is the AWS-documented pattern.
 *
 * Returns true if detected (caller should re-initiate the SSO flow).
 */
function isAccountLinkedError(): boolean {
  const params = new URLSearchParams(globalThis.location.search);
  const errorDescription = params.get("error_description");
  return errorDescription?.includes("SSO_ACCOUNT_LINKED") === true;
}

/**
 * Detects an IdP-initiated SSO flow (e.g. user clicked the Rippling tile
 * directly instead of the "Sign in with Rippling SSO" button in the app).
 *
 * When the flow is IdP-initiated, the callback has a `code` param but
 * Amplify has no stored OAuth state (because signInWithRedirect was never
 * called). Amplify's OAuth listener silently fails to exchange the code.
 *
 * Returns true if the callback has an auth code but no Amplify OAuth state.
 */
function isIdpInitiatedFlow(): boolean {
  const params = new URLSearchParams(globalThis.location.search);
  if (!params.has("code")) {
    return false;
  }

  // Amplify v6 (6.16.3) stores OAuth state in localStorage with keys
  // containing "oauthState". If no such key exists, this is an IdP-initiated
  // flow. This relies on an Amplify implementation detail — verify after upgrades.
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState")) {
      return false;
    }
  }
  return true;
}

export function RipplingSsoCallback(): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const [error, setError] = useState<string | undefined>();
  const hasHandled = useRef(false);

  // Handle two cases that require silently re-initiating the SSO flow:
  //
  // 1. SSO_ACCOUNT_LINKED: First SSO login for employees with an existing
  //    Cognito account. The PreSignUp Lambda links the Rippling identity and
  //    throws. The second attempt authenticates via the linked native user.
  //
  // 2. IdP-initiated flow: User clicked the Rippling tile directly instead
  //    of the app's SSO button. Amplify has no stored OAuth state, so it
  //    can't exchange the auth code. Re-initiating from the frontend gives
  //    Amplify a proper OAuth flow with code challenge.
  const needsRetry = isAccountLinkedError() || isIdpInitiatedFlow();

  // Limit retries to prevent infinite redirect loops if the SSO_ACCOUNT_LINKED
  // error or IdP-initiated detection keeps triggering on every callback.
  const SSO_RETRY_KEY = "sso_retry_count";
  const MAX_SSO_RETRIES = 1;
  const retryCount = Number(sessionStorage.getItem(SSO_RETRY_KEY) ?? "0");
  const shouldRetryFlow = needsRetry && retryCount < MAX_SSO_RETRIES;

  useEffect(() => {
    if (needsRetry && retryCount >= MAX_SSO_RETRIES) {
      logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_RETRY_EXHAUSTED, {
        error: new Error("SSO callback retry exhausted"),
        metadata: {
          reason: isAccountLinkedError() ? "account_linked" : "idp_initiated",
          retryCount,
        },
      });
      setError("Sign in with Rippling failed. Please try again.");
      sessionStorage.removeItem(SSO_RETRY_KEY);
      return;
    }

    if (shouldRetryFlow) {
      const retryReason = isAccountLinkedError() ? "account_linked" : "idp_initiated";
      logEvent(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_RETRY, {
        reason: retryReason,
        retryCount: retryCount + 1,
      });
      sessionStorage.setItem(SSO_RETRY_KEY, String(retryCount + 1));
      signInWithRedirect({
        provider: { custom: "RipplingEmployees" },
        options: { prompt: "LOGIN" },
      }).catch((retryError: unknown) => {
        logError(APP_V2_APP_EVENTS.RIPPLING_SSO_REDIRECT_FAILURE, { error: retryError });
        sessionStorage.removeItem(SSO_RETRY_KEY);
        setError("Sign in with Rippling failed. Please try again.");
      });
    }
  }, [shouldRetryFlow, needsRetry, retryCount]);

  // Strip authorization code and state from URL to prevent exposure
  // in browser history or referrer headers.
  useEffect(() => {
    if (!shouldRetryFlow) {
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
  }, [shouldRetryFlow]);

  useEffect(() => {
    // Skip the normal callback flow when re-initiating (account linking or IdP-initiated).
    if (shouldRetryFlow) {
      return;
    }

    let cancelled = false;

    async function completeSignIn() {
      if (hasHandled.current) {
        return;
      }
      hasHandled.current = true;

      try {
        // Hydrate the Redux session before navigating so PrivateRoute
        // sees a valid session immediately — same pattern as AuthRedirect.
        // initCognitoSession deduplicates concurrent calls, so if AppLayout's
        // useEffect also calls it, the second call returns the same promise.
        const sessionRestored = await dispatch(initCognitoSession());

        if (!sessionRestored) {
          throw new Error("Failed to restore session after SSO sign-in");
        }

        sessionStorage.removeItem(SSO_RETRY_KEY);
        logEvent(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_SUCCESS);

        const requestedUrl = localStorage.getItem(RIPPLING_SSO_REQUESTED_URL_KEY);
        localStorage.removeItem(RIPPLING_SSO_REQUESTED_URL_KEY);

        const target =
          requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

        if (!cancelled) {
          history.replace(target);
        }
      } catch (signInError: unknown) {
        logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_FAILURE, { error: signInError });
        if (!cancelled) {
          setError("Sign in with Rippling failed. Please try again.");
        }
      }
    }

    function handleFailure(errorMessage?: string) {
      if (cancelled || hasHandled.current) {
        return;
      }
      hasHandled.current = true;
      logError(APP_V2_APP_EVENTS.RIPPLING_SSO_CALLBACK_FAILURE, {
        error: new Error(errorMessage ?? "SSO sign-in failed"),
      });
      setError("Sign in with Rippling failed. Please try again.");
    }

    const unsubscribe = Hub.listen("auth", (data) => {
      if (data.payload.event === "signInWithRedirect") {
        void completeSignIn();
      }
      if (data.payload.event === "signInWithRedirect_failure") {
        handleFailure("signInWithRedirect_failure event received");
      }
    });

    // The Hub event may have already fired before our listener attached.
    // Check if a valid session already exists.
    void (async () => {
      try {
        const session = await fetchAuthSessionWithLock();
        if (session.tokens?.idToken && !hasHandled.current) {
          void completeSignIn();
        }
      } catch {
        // No existing session — wait for Hub event
      }
    })();

    const timeout = setTimeout(() => {
      handleFailure("SSO callback timed out");
    }, SSO_CALLBACK_TIMEOUT_MS);

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [dispatch, history, shouldRetryFlow]);

  if (error) {
    return (
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.grey[900],
          zIndex: 1000,
        })}
      >
        <Box
          sx={(theme) => ({
            padding: theme.spacing(6, 8),
            width: 420,
            minHeight: 300,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          })}
        >
          <Logo />
          <Box
            sx={(theme) => ({
              marginTop: theme.spacing(4),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: theme.spacing(3),
              width: "100%",
            })}
          >
            <Text color="error" sx={{ textAlign: "center" }}>
              {error}
            </Text>
            <Link to="/enter" style={{ fontSize: 14 }}>
              Back to Login
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.grey[900],
        zIndex: 1000,
      })}
    >
      <Box
        sx={(theme) => ({
          padding: theme.spacing(6, 8),
          width: 420,
          minHeight: 300,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <Logo />
        <Box
          sx={(theme) => ({
            marginTop: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.spacing(3),
            width: "100%",
          })}
        >
          <CircularProgress />
          <Text>Signing in...</Text>
        </Box>
      </Box>
    </Box>
  );
}
